import { Stack, TextField, Typography } from '@mui/material';
import { useController, useFormContext } from 'react-hook-form';
import { FormSelect, FormTextField } from 'src/components/Forms';
import {
  CreateDraftWorkflowVersionField,
  type CreateDraftWorkflowVersionFormValues,
} from 'src/pages/Settings/workflows/components/create-draft-version-form';

const OUTLINED_FIELD_PROPS = {
  fullWidth: true,
  size: 'small',
  variant: 'outlined',
} as const;

const DEFINITION_TEXTAREA_ROWS = 14;

const validateWorkflowDefinition = (value: string) => {
  if (!value.trim()) {
    return 'Definition is required';
  }

  try {
    JSON.parse(value);
    return true;
  } catch {
    return 'Invalid JSON format';
  }
};

interface WorkflowOption {
  workflowId: string;
  name: string;
}

interface CreateDraftWorkflowVersionFieldsProps {
  disabled: boolean;
  workflows: WorkflowOption[];
}

const WorkflowDefinitionField = ({ disabled }: { disabled: boolean }) => {
  const { control } = useFormContext<CreateDraftWorkflowVersionFormValues>();
  const {
    field,
    fieldState: { error },
  } = useController({
    name: CreateDraftWorkflowVersionField.Definition,
    control,
    rules: {
      validate: validateWorkflowDefinition,
    },
  });

  return (
    <TextField
      {...OUTLINED_FIELD_PROPS}
      label="Workflow Definition (JSON)"
      multiline
      rows={DEFINITION_TEXTAREA_ROWS}
      fullWidth
      disabled={disabled}
      error={!!error}
      helperText={error?.message}
      value={field.value ?? ''}
      onChange={field.onChange}
      onBlur={field.onBlur}
      inputRef={field.ref}
      InputProps={{
        sx: {
          alignItems: 'flex-start',
        },
      }}
      inputProps={{
        style: {
          fontFamily: 'IBM Plex Mono, ui-monospace, SFMono-Regular, Menlo, monospace',
        },
      }}
    />
  );
};

export const CreateDraftWorkflowVersionFields = ({
  disabled,
  workflows,
}: CreateDraftWorkflowVersionFieldsProps) => {
  const workflowOptions: Option[] = workflows.map((workflow) => ({
    id: workflow.workflowId,
    value: workflow.workflowId,
    label: workflow.name,
  }));

  return (
    <>
      <Stack sx={{ mt: 3 }}>
        <FormSelect
          type="select"
          name={CreateDraftWorkflowVersionField.WorkflowId}
          label="Workflow"
          required="Please select a workflow"
          disabled={disabled}
          fullWidth
          options={workflowOptions}
          variant="outlined"
          labelProps={{ shrink: true }}
          showErrorText
          selectProps={{
          ...OUTLINED_FIELD_PROPS,
          displayEmpty: true,
          renderValue: (selected) => {
            if (!selected) {
              return <Typography color="text.secondary">Select Workflow</Typography>;
            }

              return (
                workflowOptions.find((option) => option.value === selected)?.label ??
                String(selected)
              );
            },
          }}
        />
      </Stack>

      <FormTextField
        type="textfield"
        name={CreateDraftWorkflowVersionField.Description}
        label="Description (Optional)"
        disabled={disabled}
        fullWidth
        variant="outlined"
        textFieldProps={{
          ...OUTLINED_FIELD_PROPS,
          multiline: true,
          minRows: 2,
          maxRows: 4,
          placeholder: 'e.g., "Updated email trigger logic"',
        }}
      />

      <WorkflowDefinitionField disabled={disabled} />
    </>
  );
};
